import * as Sentry from '@sentry/core';
import {
  type RetryableFetchParams,
  useRetryableFetch,
} from '@vrplatform/utils';
import createClient from 'openapi-fetch';
import { useCache } from './cache';
import { getControlPlaneRequestUrl } from './control-plane-routing';
import type { paths } from './generated/v1';
import { retargetRequest } from './retarget-request';
import { generateApiKeySec } from './sec';
import type { ApiClient, ApiClientAuth } from './types';

export function useApiClient({
  baseUrl: base,
  local,
  auth,
  sessionId: sid,
  // headers: h,
  testing,
  headers: h,
  ...retryableFetchParams
}: {
  /** Maximum attempts for GET, HEAD, and OPTIONS. Writes are attempted once. */
  attempts?: number;
  auth?: ApiClientAuth | (() => ApiClientAuth);
  baseUrl?: string;
  sessionId?: string;
  local?: boolean | number;
  headers?: Record<string, string> | (() => Record<string, string>);
  testing?: {
    overwriteUri?: string;
    getOverwriteTimestamp?: () => number | undefined;
  };
} & RetryableFetchParams) {
  const baseUrl =
    (local
      ? `http://localhost:${typeof local === 'number' ? local : 8877}`
      : base) || 'https://api.vrplatform.app';

  const sessionId = sid || crypto.randomUUID();
  // cache the sec key for 1 minute (its valid for 5 minutes on the API)
  const cache = useCache(
    async (v: string) =>
      generateApiKeySec(v, testing?.getOverwriteTimestamp?.()),
    60_000
  );

  const trace = Sentry.getTraceData();
  const client = createClient<paths>({
    baseUrl,
    querySerializer: {
      array: {
        explode: false,
        style: 'form',
      },
    },
    fetch(req) {
      const isRead = ['GET', 'HEAD', 'OPTIONS'].includes(req.method);
      return useRetryableFetch({
        ...retryableFetchParams,
        attempts: isRead ? (retryableFetchParams.attempts ?? 5) : 1,
        assertOnLastAttempt:
          retryableFetchParams.assertOnLastAttempt ??
          (!isRead && retryableFetchParams.assertResponse !== undefined),
        onRetry(arg) {
          cache.invalidate();
          return retryableFetchParams.onRetry?.(arg);
        },
      })(async () => {
        let clone = req.clone() as Request;
        const url = getControlPlaneRequestUrl(req.method, new URL(req.url));
        if (url.href !== req.url) clone = await retargetRequest(clone, url);
        if (testing?.overwriteUri)
          clone = await retargetRequest(clone, testing.overwriteUri);

        const a = (typeof auth === 'function' ? auth() : auth) || {};
        if (sessionId) clone.headers.set('X-Session-Id', sessionId);
        if (a.sec)
          clone.headers.set('X-Api-Key', `Sec ${await cache.current(a.sec)}`);
        if (a.apiKey) clone.headers.set('X-Api-Key', a.apiKey);
        if (a.tenantId) clone.headers.set('X-Team-Id', a.tenantId);
        if (a.accessToken)
          clone.headers.set(
            'Authorization',
            a.accessToken.startsWith('Bearer ')
              ? a.accessToken
              : `Bearer ${a.accessToken}`
          );
        const additionalHeaders = h ? (typeof h === 'function' ? h() : h) : {};
        for (const key in additionalHeaders) {
          clone.headers.set(key, additionalHeaders[key]);
        }

        if (trace['sentry-trace'])
          clone.headers.set('sentry-trace', trace['sentry-trace']);
        if (trace.baggage) clone.headers.set('baggage', trace.baggage);

        return clone;
      });
    },
  });

  return Object.assign(client, { sessionId }) satisfies ApiClient;
}
